import React, { useEffect } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { PreQualificationStep, setPreQualificationStep, setSignInStep, SignInStep } from 'actions';
import PrivateRoute from 'routes/PrivateRoute';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import useBackListener from 'hooks/useBackListener';
import useScript from 'hooks/useScript';
import { useSentryContext } from 'hooks/useSentryContext';
import useAutoScroll from 'hooks/useScrollUp';
import Dashboard from 'pages/Dashboard';
import Error from 'pages/Error/Error';
import Main from 'pages/Main';
import NotFound from 'pages/NotFound';
import PreQualification from 'pages/PreQualification';
import AcknowledgementAndConsent from 'pages/PreQualification/steps/AcknowledgementAndConsent';
import CompleteMobileAuth from 'pages/PreQualification/steps/CompleteMobileAuth';
import VerificationComplete from 'pages/PreQualification/steps/VerificationComplete';
import SignInProcess from 'pages/SignInProcess';
import EnterNewPassword from 'pages/SignInProcess/steps/EnterNewPassword';
import NotQualified from 'pages/NotQualified/NotQualified';
import { PasswordProtection } from 'pages/PasswordProtection/PasswordProtection';
import { PasswordProtectedRoute } from 'pages/PasswordProtection/PasswordProtectedRoute';
import MobileSession from 'pages/Dashboard/Identity/ValidationId/MobileSession';
import { ApplicationEnvironments } from 'enums/common.enums';
import { ENVIRONMENT, IS_CONCEPTUAL_HQ_ENABLED } from 'constants/constants';
import RedirectToNewContractorPortal from 'pages/ContractorPortal/RedirectToNewContractorPortal';
import ResumeProcessLink from 'pages/ResumeProcessLink';
import { cacheUrlMarketingParamsToCookie, connectConceptualHqSnippet, triggerHotjarEvent } from 'routes/routes.utils';

import { AppRoutes } from './routes.enums';

export default () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const { currentPreQualificationStep, currentSignInStep, currentDashboardStep } = useAppSelector(
    (state) => state.stages,
  );
  const loanType = useAppSelector((state) => state.application[LoanInformationVariables.LoanType]);
  const { pathname, search: query } = useLocation();

  useSentryContext();

  useEffect(() => {
    if (!history?.state?.usr?.hfsPortal) {
      navigator(`${pathname}${query}`, { state: { hfsPortal: true } });
    }

    if (IS_CONCEPTUAL_HQ_ENABLED) {
      connectConceptualHqSnippet();
    }
  }, []);

  useEffect(() => {
    triggerHotjarEvent(pathname, currentPreQualificationStep);
    cacheUrlMarketingParamsToCookie();
  }, [pathname, currentPreQualificationStep, currentSignInStep, currentDashboardStep]);

  useBackListener(() => {
    setTimeout(() => {
      if (pathname === AppRoutes.Prequalification) {
        switch (currentPreQualificationStep) {
          case PreQualificationStep.BorrowerInfo:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
            break;
          case PreQualificationStep.BorrowerIncome:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
            break;
          case PreQualificationStep.CoBorrowerInfo:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
            break;
          case PreQualificationStep.CoBorrowerIncome:
            dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
            break;
          case PreQualificationStep.BorrowerPhoneVerification:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
            break;
          case PreQualificationStep.CoBorrowerPhoneVerification:
            dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
            break;
          case PreQualificationStep.Contractor:
            dispatch(
              setPreQualificationStep(
                loanType === ApplicationType.Joint
                  ? PreQualificationStep.CoBorrowerIncome
                  : PreQualificationStep.BorrowerIncome,
              ),
            );
            break;
        }
      }

      if (pathname === AppRoutes.Login || pathname === AppRoutes.IdentityVerification) {
        switch (currentSignInStep) {
          case SignInStep.ForgotPassword:
            dispatch(setSignInStep(SignInStep.SignIn));
            break;
          case SignInStep.SentResetPasswordLink:
            dispatch(setSignInStep(SignInStep.ForgotPassword));
            break;
          case SignInStep.SignIn:
            navigator(AppRoutes.Prequalification);

            return;
        }
      }

      if (pathname === AppRoutes.Dashboard) {
        return navigator(AppRoutes.Dashboard);
      }
    }, 0);
  });
  useScript(`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&v=weekly`);

  if (ENVIRONMENT === ApplicationEnvironments.Production) {
    useScript(
      `https://tag.pearldiver.io/ldc.js?pid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_PID}&aid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_AID}`,
    );
  }

  useAutoScroll();

  return (
    <Routes>
      <Route path={''} element={<PasswordProtectedRoute />}>
        <Route path={AppRoutes.ContractorPortal} element={<RedirectToNewContractorPortal />} />
        <Route element={<Main />}>
          <Route path={AppRoutes.Home} element={<Navigate replace to={`${AppRoutes.Prequalification}${query}`} />} />
          <Route path={AppRoutes.Prequalification} element={<PreQualification />} />
          <Route path={AppRoutes.AcknowledgementAndConsent} element={<AcknowledgementAndConsent />} />
          <Route path={AppRoutes.Dashboard} element={<PrivateRoute />}>
            <Route path={AppRoutes.Dashboard} element={<Dashboard />} />
          </Route>
          <Route path={AppRoutes.LoanPortal} element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />} />
          <Route path={AppRoutes.ResumeProcess} element={<ResumeProcessLink />} />
          <Route
            path={AppRoutes.OfferProcessing}
            element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />}
          />
          <Route path={AppRoutes.Login} element={<SignInProcess />} />
          <Route path={AppRoutes.IdentityVerification} element={<SignInProcess differentEmail />} />
          <Route path={AppRoutes.ResetPassword + `/:token`} element={<EnterNewPassword />} />
          <Route path={AppRoutes.CompleteAuthentication} element={<VerificationComplete />} />
          <Route path={AppRoutes.CompleteMobileAuthentication} element={<CompleteMobileAuth />} />
          <Route path={AppRoutes.Other} element={<NotFound />} />
          <Route path={AppRoutes.Error} element={<Error />} />
          <Route path={AppRoutes.NotQualified} element={<NotQualified />} />
          <Route path={AppRoutes.ValidationSession} element={<MobileSession />} />
        </Route>
      </Route>
      <Route path={AppRoutes.PasswordProtection} element={<PasswordProtection />} />
    </Routes>
  );
};
